import { PolarAngleAxis, RadialBar, RadialBarChart, ResponsiveContainer } from 'recharts';
import { formatUsd } from '@kioskgaming/ui';

type Props = {
  /** 0–100, already clamped by the caller. */
  percent: number;
  primaryLabel: string;
  primaryValue: number;
  secondaryLabel: string;
  secondaryValue: number;
  valueFormatter?: (value: number) => string;
};

/**
 * Collection-rate dial (Admin-style). Arc shows how much cash expected was received.
 */
export default function GaugeChart({
  percent,
  primaryLabel,
  primaryValue,
  secondaryLabel,
  secondaryValue,
  valueFormatter = formatUsd,
}: Props) {
  const complete = percent >= 99.95;
  const arcColor = complete ? '#10b981' : percent >= 80 ? '#3b82f6' : '#f59e0b';

  return (
    <div className="flex h-full min-h-[11rem] items-center gap-3">
      <div className="relative h-44 w-1/2 min-w-[128px]">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            data={[{ name: 'collected', value: percent, fill: arcColor }]}
            innerRadius="68%"
            outerRadius="100%"
            startAngle={210}
            endAngle={-30}
            barSize={14}
          >
            <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
            <RadialBar
              background={{ fill: '#eff6ff' }}
              dataKey="value"
              cornerRadius={8}
              isAnimationActive={false}
            />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-semibold tabular-nums text-blue-950">
            {percent.toFixed(percent % 1 === 0 ? 0 : 1)}%
          </span>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">collected</span>
        </div>
      </div>

      <dl className="flex-1 space-y-3 text-xs">
        <div>
          <dt className="text-muted-foreground">{primaryLabel}</dt>
          <dd className="text-sm font-semibold tabular-nums text-blue-950">{valueFormatter(primaryValue)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{secondaryLabel}</dt>
          <dd
            className={
              secondaryValue > 0
                ? 'text-sm font-semibold tabular-nums text-rose-700'
                : 'text-sm font-semibold tabular-nums text-blue-950'
            }
          >
            {valueFormatter(secondaryValue)}
          </dd>
        </div>
      </dl>
    </div>
  );
}
